/**
 * Signal plugin for axos RecordEngine.
 *
 * Passively consumes axos event batches and offloads inference
 * computation to a Web Worker (parsing, element extraction, vector
 * building, accumulation, HTTP sync all run off the main thread).
 *
 * Falls back to main-thread processing when Web Workers are
 * unavailable (e.g. restrictive CSP, older browsers).
 *
 * Session state is persisted in sessionStorage so the same session
 * survives page reloads.  A session is considered dead after 5 min
 * of inactivity (no successful sync), mirroring the inspect library.
 *
 * No raw recording data ever leaves the browser.
 */

import type { AxosPlugin } from "axos/record";
import type { WrappedEvent } from "axos/record";
import type { RecordEngine } from "axos/record";
import { WorkerBridge } from "./worker/bridge";
import { SignalAccumulator } from "./inference/accumulator";
import { SignalSync } from "./sync";

declare const __SIGNAL_WORKER_BLOB__: string | undefined;

const SESSION_ID_KEY = "__recapt_signal_sid";
const LAST_SYNC_KEY = "__recapt_signal_lrt";
const SESSION_DEAD_MS = 5 * 60 * 1000;

export interface SignalPluginOptions {
  /** Base API URL (e.g. "https://api.recapt.dev/v1beta"). */
  apiUrl: string;

  /** Public API key for the organization. */
  apiKey: string;

  /** How often to POST inference data (ms). Default: 2000. */
  syncIntervalMs?: number;

  /** Enable debug logging. Default: false. */
  debug?: boolean;
}

function hasSessionStorage(): boolean {
  try {
    const k = "__signal_test";
    sessionStorage.setItem(k, "1");
    sessionStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

export class SignalPlugin implements AxosPlugin {
  readonly name = "signal";

  private bridge: WorkerBridge | null = null;
  private accumulator: SignalAccumulator | null = null;
  private sync: SignalSync | null = null;
  private isDebug: boolean;
  private opts: SignalPluginOptions;
  private engine: RecordEngine | null = null;
  private unloadHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private canPersist = false;

  constructor(opts: SignalPluginOptions) {
    this.opts = opts;
    this.isDebug = opts.debug ?? false;
  }

  setup(engine: RecordEngine): void {
    this.engine = engine;
    this.canPersist = typeof window !== "undefined" && hasSessionStorage();

    const { sessionId, isExpired } = this.loadSession();

    if (isExpired && sessionId) {
      if (this.isDebug) {
        console.debug(
          "[signal] Previous session expired (>5 min idle), starting fresh",
        );
      }
      this.clearSession();
    }

    const activeSessionId = isExpired ? null : sessionId;

    const workerStarted = this.tryStartWorker(activeSessionId);

    if (!workerStarted) {
      if (this.isDebug) {
        console.debug(
          "[signal] Worker unavailable, using main-thread fallback",
        );
      }
      this.initMainThreadFallback(activeSessionId);
    }

    if (typeof window !== "undefined") {
      this.unloadHandler = () => {
        this.flush();
        this.persistLastSyncTime();
      };
      this.visibilityHandler = () => {
        if (document.visibilityState === "hidden") {
          this.flush();
          this.persistLastSyncTime();
        }
      };
      window.addEventListener("beforeunload", this.unloadHandler);
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }

    if (this.isDebug) {
      console.debug(
        `[signal] Plugin initialized (${workerStarted ? "worker" : "main-thread"})` +
          (activeSessionId
            ? ` resuming session ${activeSessionId}`
            : " new session"),
      );
    }
  }

  onEvents(events: WrappedEvent[]): void {
    const raw = events as unknown as Record<string, unknown>[];
    const url = typeof location !== "undefined" ? location.href : "";
    const title = typeof document !== "undefined" ? document.title : "";

    if (this.bridge?.isActive) {
      this.bridge.postEvents(raw, url, title);
    } else if (this.accumulator && this.sync) {
      const result = this.accumulator.processEvents(raw);
      if (result) {
        this.sync.enqueue(result.request);
      }
    }
  }

  onSnapshot(): void {
    // Snapshots flow through onEvents as part of the normal batch.
  }

  destroy(): void {
    this.flush();
    this.persistLastSyncTime();

    if (this.bridge) {
      this.bridge.destroy();
      this.bridge = null;
    }

    if (this.sync) {
      this.sync.stop();
      this.sync = null;
    }

    if (this.accumulator) {
      this.accumulator.reset();
      this.accumulator = null;
    }

    if (typeof window !== "undefined") {
      if (this.unloadHandler) {
        window.removeEventListener("beforeunload", this.unloadHandler);
        this.unloadHandler = null;
      }
      if (this.visibilityHandler) {
        document.removeEventListener(
          "visibilitychange",
          this.visibilityHandler,
        );
        this.visibilityHandler = null;
      }
    }

    if (this.isDebug) {
      console.debug("[signal] Plugin destroyed");
    }

    this.engine = null;
  }

  /** Manually set the session ID (e.g. from an existing session). */
  setSessionId(id: string): void {
    if (this.bridge?.isActive) {
      this.bridge.setSessionId(id);
    } else if (this.sync) {
      this.sync.setSessionId(id);
    }
    this.persistSessionId(id);
  }

  // ---------------------------------------------------------------------------
  // Session persistence (mirrors @glimt/inspect behaviour)
  // ---------------------------------------------------------------------------

  private loadSession(): { sessionId: string | null; isExpired: boolean } {
    if (!this.canPersist) return { sessionId: null, isExpired: false };

    const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    const lrt = sessionStorage.getItem(LAST_SYNC_KEY);

    if (!sessionId) return { sessionId: null, isExpired: false };

    if (!lrt) return { sessionId, isExpired: false };

    const elapsed = Date.now() - parseInt(lrt, 10);
    return { sessionId, isExpired: elapsed >= SESSION_DEAD_MS };
  }

  private persistSessionId(id: string): void {
    if (!this.canPersist) return;
    sessionStorage.setItem(SESSION_ID_KEY, id);
    sessionStorage.setItem(LAST_SYNC_KEY, Date.now().toString());
  }

  private persistLastSyncTime(): void {
    if (!this.canPersist) return;
    sessionStorage.setItem(LAST_SYNC_KEY, Date.now().toString());
  }

  private clearSession(): void {
    if (!this.canPersist) return;
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(LAST_SYNC_KEY);
  }

  private onSessionIdReceived = (id: string): void => {
    this.persistSessionId(id);
    if (this.isDebug) {
      console.debug("[signal] Session ID persisted:", id);
    }
  };

  private onSyncSuccess = (): void => {
    this.persistLastSyncTime();
  };

  // ---------------------------------------------------------------------------
  // Worker / fallback setup
  // ---------------------------------------------------------------------------

  private flush(): void {
    if (this.bridge?.isActive) {
      this.bridge.flush();
    } else if (this.sync) {
      this.sync.flush();
    }
  }

  private tryStartWorker(existingSessionId: string | null): boolean {
    if (typeof Worker === "undefined") return false;

    let workerBlob: string | undefined;
    try {
      workerBlob =
        typeof __SIGNAL_WORKER_BLOB__ !== "undefined"
          ? __SIGNAL_WORKER_BLOB__
          : undefined;
    } catch {
      // __SIGNAL_WORKER_BLOB__ not defined — non-browser build
    }

    if (!workerBlob) return false;

    try {
      const blob = new Blob([workerBlob], { type: "application/javascript" });
      const blobUrl = URL.createObjectURL(blob);

      this.bridge = new WorkerBridge({
        apiUrl: this.opts.apiUrl,
        apiKey: this.opts.apiKey,
        syncIntervalMs: this.opts.syncIntervalMs ?? 2000,
        debug: this.isDebug,
        sessionId: existingSessionId,
        onSessionId: this.onSessionIdReceived,
        onSyncSuccess: this.onSyncSuccess,
      });

      this.bridge.start(blobUrl);
      return this.bridge.isActive;
    } catch {
      if (this.isDebug) {
        console.warn("[signal] Failed to create worker");
      }
      this.bridge = null;
      return false;
    }
  }

  private initMainThreadFallback(existingSessionId: string | null): void {
    this.accumulator = new SignalAccumulator();
    this.sync = new SignalSync({
      apiUrl: this.opts.apiUrl,
      apiKey: this.opts.apiKey,
      syncIntervalMs: this.opts.syncIntervalMs ?? 2000,
      debug: this.isDebug,
      onSessionId: this.onSessionIdReceived,
      onSyncSuccess: this.onSyncSuccess,
    });
    if (existingSessionId) {
      this.sync.setSessionId(existingSessionId);
    }
    this.sync.start();
  }
}

/** Factory function for cleaner API. */
export function createSignalPlugin(opts: SignalPluginOptions): SignalPlugin {
  return new SignalPlugin(opts);
}
